/**
 * Screen-reader mirror for the 3D constellation (doc 147 §5.3 a11y).
 *
 * Pure builder: takes the manifest and the focused id, returns a tree the
 * `ConstellationMirror` component renders as a visually-hidden navigable
 * list. AT users hear the same structure sighted users see in the 3D scene
 * — the 3D canvas itself is opaque to screen readers.
 */
import type { AtlasManifest, Reference } from '@atlas-manifest/types';

export interface MirrorReference {
  from: string;
  to: string;
  fromField: string;
}

export interface MirrorEntity {
  id: string;
  name: string;
  focused: boolean;
  /** References where this entity is the source. */
  outgoing: MirrorReference[];
  /** References where this entity is the target. */
  incoming: MirrorReference[];
}

export interface MirrorDomain {
  id: string;
  name: string;
  entities: MirrorEntity[];
}

export interface A11yMirror {
  /** One-line summary suitable for an aria-live region. */
  summary: string;
  domains: MirrorDomain[];
}

export function buildA11yMirror(
  manifest: AtlasManifest,
  focusedId: string | null,
): A11yMirror {
  const byDomain = new Map<string, MirrorEntity[]>();

  for (const e of manifest.entities) {
    const outgoing: MirrorReference[] = manifest.references
      .filter((r) => r.from === e.id)
      .map(toMirror);
    const incoming: MirrorReference[] = manifest.references
      .filter((r) => r.to === e.id)
      .map(toMirror);
    const entry: MirrorEntity = {
      id: e.id,
      name: e.name,
      focused: e.id === focusedId,
      outgoing,
      incoming,
    };
    const arr = byDomain.get(e.domain) ?? [];
    arr.push(entry);
    byDomain.set(e.domain, arr);
  }

  const domains: MirrorDomain[] = manifest.domains
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((d) => ({
      id: d.id,
      name: d.name,
      entities: (byDomain.get(d.id) ?? []).slice().sort((a, b) => a.id.localeCompare(b.id)),
    }));

  const focusPhrase = focusedId ? `. Focused on ${focusedId}` : '';
  const summary =
    `Brisarx Atlas — ${manifest.entities.length} entities across ` +
    `${manifest.domains.length} domains${focusPhrase}.`;

  return { summary, domains };
}

function toMirror(r: Reference): MirrorReference {
  return { from: r.from, to: r.to, fromField: r.fromField };
}
